import { states } from '@/constants/lifecycle';
import withDraft from '@/utils/draftHelper';
import { responseError } from '@/utils/errors';
import findResult, {
  assignIfExists,
  assignProps,
  findItemByID,
  mapItemOrderByValues,
  removeItemArr,
  removeItemByID,
  removeItemsArr,
} from '@/utils/resultsHelper';
import findDataset, { findAllDatasetsByMetadata } from '../../state/shared';
import { activeDictionary, pruneActiveDictionaryErrors } from '../../dictionaries/state/state';
import actions from '../constants';
import attachmentsInitialState, {
  activeAttachmentInitialState,
  activeFolderInitialState,
  createFolderInitialState,
  dirtyAttachments,
  downloadableAttachmentConfirmationState,
  findDatasetAttachments,
  pruneAttachmentErrors,
} from './state';

const datasetAttachmentDownload = withDraft((draft, { category, code, attachment }) => {
  const { item, attachments } = findDatasetAttachments(draft, { category, code });

  attachments.downloadsInProgress.push(attachment);
  item.isDirty.attachments = dirtyAttachments(attachments);
});

const datasetAttachmentDownloadSuccessOrError = withDraft((draft, { category, code, attachment }) => {
  const { item, attachments } = findDatasetAttachments(draft, { category, code });

  attachments.downloadsInProgress = removeItemByID(attachments.downloadsInProgress, attachment);
  item.isDirty.attachments = dirtyAttachments(attachments);
});

const datasetAttachmentsUpload = withDraft((draft, { category, code, files }) => {
  const { item, attachments } = findDatasetAttachments(draft, { category, code });

  if (item) {
    attachments.uploadsInProgress = [...attachments.uploadsInProgress, ...files];
    attachments.uploadStatus = states.PENDING;
    item.isDirty.attachments = dirtyAttachments(attachments);
  }
});

const datasetAttachmentsUploadFail = withDraft((draft, { category, code, files }) => {
  const { item, attachments } = findDatasetAttachments(draft, { category, code });

  if (attachments) {
    attachments.uploadStatus = states.ERROR;
    attachments.uploadsInProgress = removeItemsArr(attachments.uploadsInProgress, files.map(({ id }) => id));
    item.isDirty.attachments = dirtyAttachments(attachments);
  }
});

const datasetAttachmentUploadRemoved = withDraft((draft, { category, code, file }) => {
  const { item, attachments } = findDatasetAttachments(draft, { category, code });

  // Uppy removed event can be fired on unmounts
  // not guaranteed to have attachments in state
  if (attachments) {
    attachments.uploadStatus = states.ERROR;
    attachments.uploadsInProgress = removeItemArr(attachments.uploadsInProgress, file.id);
    item.isDirty.attachments = dirtyAttachments(attachments);
  }
});

const datasetAttachmentUploadSuccess = withDraft((draft, { category, code, file }) => {
  const { item, attachments } = findDatasetAttachments(draft, { category, code });

  if (attachments) {
    attachments.uploadStatus = states.SUCCESS;
    attachments.uploadsInProgress = removeItemArr(attachments.uploadsInProgress, file.id);
    item.isDirty.attachments = dirtyAttachments(attachments);
  }
});

const deleteDatasetAttachment = withDraft((draft, action) => {
  const { categories, codes } = findAllDatasetsByMetadata(draft, action);

  categories.forEach((category) => {
    codes.forEach((code) => {
      const item = findResult(draft.datasets, category, code);
      if (item?.attachments) {
        action.items.forEach((attachment) => {
          const attachmentItem = findItemByID(item.attachments.items, attachment);
          if (attachmentItem) {
            attachmentItem.destroyStatus = states.PENDING;
            attachmentItem.destroyErrors = [];
          }
        });
      }
    });
  });
});

const deleteDatasetAttachmentSuccess = withDraft((draft, action) => {
  const { categories, codes } = findAllDatasetsByMetadata(draft, action);

  draft.deleteDatasetAttachmentOpen = false;
  categories.forEach((category) => {
    codes.forEach((code) => {
      const item = findResult(draft.datasets, category, code);
      if (item?.attachments) {
        action.items.forEach((attachment) => {
          item.attachments.items = removeItemByID(item.attachments.items, attachment);
        });
      }
    });
  });
});

const deleteDatasetAttachmentError = withDraft((draft, action) => {
  const { categories, codes } = findAllDatasetsByMetadata(draft, action);

  draft.deleteDatasetAttachmentOpen = false;
  categories.forEach((category) => {
    codes.forEach((code) => {
      const item = findResult(draft.datasets, category, code);
      if (item?.attachments) {
        action.items.forEach((attachment) => {
          const attachmentItem = findItemByID(item.attachments.items, attachment);
          if (attachmentItem) {
            attachmentItem.destroyStatus = states.ERROR;
            attachmentItem.destroyErrors = [attachment.error];
          }
        });
      }
    });
  });
});

const fetchDatasetAttachments = withDraft((draft, { category, code }) => {
  const { item, attachments } = findDatasetAttachments(draft, { category, code });

  if (item) {
    attachments.readStatus = states.PENDING;
    attachments.readErrors = [];
    attachments.items = [];
  } else {
    const missingDataset = { code, attachments: { ...attachmentsInitialState() } };

    missingDataset.attachments.readStatus = states.PENDING;
    missingDataset.attachments.readErrors = [];

    draft.datasets[category].results.push(missingDataset);
  }
});

const fetchDatasetAttachmentsSuccess = withDraft((draft, { category, code, result }) => {
  const { attachments } = findDatasetAttachments(draft, { category, code });

  if (attachments) {
    attachments.readStatus = states.SUCCESS;
    attachments.readErrors = [];
    attachments.items = result.items;
  }
});

const fetchDatasetAttachmentsError = withDraft((draft, action) => {
  const { attachments } = findDatasetAttachments(draft, action);

  if (attachments) {
    attachments.readStatus = states.ERROR;
    attachments.readErrors = [responseError(action)];
  }
});

const updateDatasetAttachmentMetadata = withDraft((draft, { category, code, attachmentId }) => {
  const { attachments } = findDatasetAttachments(draft, { category, code });

  if (attachments) {
    draft.datasets.downloadableAttachmentConfirmation = downloadableAttachmentConfirmationState();

    attachments.updateStatus = states.PENDING;
    attachments.updateErrors = [];
    attachments.updatingItems[attachmentId] = true;
  }
});

const updateDatasetAttachmentMetadataError = withDraft((draft, action) => {
  const { attachments } = findDatasetAttachments(draft, action);

  if (attachments) {
    attachments.updateStatus = states.ERROR;
    attachments.updateErrors = [responseError(action)];
    delete attachments.updatingItems[action.attachmentId];
  }
});

const updateDatasetAttachmentMetadataSuccess = withDraft((draft, {
  category,
  code,
  attachmentId,
  result,
}) => {
  const { attachments } = findDatasetAttachments(draft, { category, code });

  if (attachments) {
    attachments.updateStatus = states.SUCCESS;
    attachments.updateErrors = [];
    delete attachments.updatingItems[attachmentId];

    attachments.items = attachments.items.map(
      (attachment) => ((attachment.id === attachmentId) ? { ...attachment, ...result } : attachment),
    );
  }
});

const toggleDatasetAttachmentColumnOrderBy = withDraft((draft, { category, code, orderByColumn }) => {
  const { attachments } = findDatasetAttachments(draft, { category, code });

  if (attachments) {
    attachments.orderBy = mapItemOrderByValues(attachments.orderBy, orderByColumn, true);
  }
});

const toggleDatasetAttachmentTypeFilter = withDraft((draft, { category, code, attachmentType }) => {
  const { filters } = findDatasetAttachments(draft, { category, code });

  if (filters) {
    if (filters.byType[attachmentType]) {
      delete filters.byType[attachmentType];
    } else { filters.byType[attachmentType] = true; }
  }
});

const updateDatasetAttachmentTextFilter = withDraft((draft, { category, code, text }) => {
  const { filters } = findDatasetAttachments(draft, { category, code });

  if (filters) {
    filters.byTextInput = text;
  }
});

const showDeleteDatasetAttachment = withDraft((draft, { category, code, attachment }) => {
  const item = findDataset(draft, { category, code });

  draft.deleteDatasetAttachmentOpen = true;
  draft.datasets.attachmentsDeletion.results = [findItemByID(item.attachments.items, attachment)];
});

const hideDeleteDatasetAttachment = withDraft((draft) => {
  draft.deleteDatasetAttachmentOpen = false;
  draft.datasets.attachmentsDeletion.results = [];
});

const showConvertAttachmentDialog = withDraft((draft, { category, code, attachment }) => {
  draft.activeDictionary.open = true;
  draft.activeDictionary.code = code;
  draft.activeDictionary.category = category;
  draft.activeDictionary.dictionaryFromDatafile.readStatus = states.PENDING;
  draft.activeDictionary.attachment = attachment;
});

const showDatasetAttachmentDictionary = withDraft((draft, { dictionary }) => {
  draft.activeDictionary.dictionaryFromDatafile.items.push(dictionary);
  draft.activeDictionary.dictionaryFromDatafile.readStatus = states.SUCCESS;
});

const showDatasetAttachmentDictionaryError = withDraft((draft, action) => {
  draft.activeDictionary.dictionaryFromDatafile.readStatus = states.ERROR;
  draft.activeDictionary.dictionaryFromDatafile.readErrors = [responseError(action)];
});

const hideConvertDatafileDialog = withDraft((draft) => { draft.activeDictionary = activeDictionary(); });

const updateActiveDictionaryAttachmentProperties = withDraft((draft, action) => {
  assignIfExists(draft.activeDictionary.dictionaryFromDatafile, action.properties);
  draft.activeDictionary.saveErrors = pruneActiveDictionaryErrors(draft.activeDictionary.saveErrors, action);
});

const showDownloadableConfirmationDialog = withDraft((draft, {
  category, code, attachmentId, isFolder,
}) => {
  draft.datasets.downloadableAttachmentConfirmation.open = true;
  draft.datasets.downloadableAttachmentConfirmation.category = category;
  draft.datasets.downloadableAttachmentConfirmation.code = code;
  draft.datasets.downloadableAttachmentConfirmation.attachmentId = attachmentId;
  draft.datasets.downloadableAttachmentConfirmation.isFolder = !!isFolder;
});

const hideDownloadableConfirmationDialog = withDraft((draft) => {
  draft.datasets.downloadableAttachmentConfirmation = downloadableAttachmentConfirmationState();
});

const showEditDatasetAttachmentDialog = withDraft((draft, { category, code, attachment }) => {
  const item = findDataset(draft, { category, code });

  draft.activeAttachment.open = true;
  draft.activeAttachment.code = code;
  draft.activeAttachment.attachment = findItemByID(item.attachments.items, attachment);
});

const hideEditDatasetAttachmentDialog = withDraft((draft) => {
  draft.activeAttachment = activeAttachmentInitialState();
});

const updateDatasetAttachmentProperties = withDraft((draft, action) => {
  const item = draft.activeAttachment;
  draft.activeAttachment.isDirty = true;
  assignProps(item, action.properties);
  draft.activeAttachment.updateErrors = pruneAttachmentErrors(draft.activeAttachment.updateErrors, action);
});

const updateDatasetAttachmentDescription = withDraft((draft) => {
  draft.activeAttachment.updateStatus = states.PENDING;
});

const updateDatasetAttachmentDescriptionError = withDraft((draft, action) => {
  const item = draft.activeAttachment.attachment;

  if (item) {
    draft.activeAttachment.updateErrors = [responseError(action)];
    draft.activeAttachment.updateStatus = states.ERROR;
  }
});

const updateDatasetAttachmentDescriptionSuccess = withDraft((draft) => {
  draft.activeAttachment = activeAttachmentInitialState();
});

const attachmentFolderPush = withDraft((draft, { category, code, folder }) => {
  const { attachments } = findDatasetAttachments(draft, { category, code });
  if (attachments) {
    attachments.folders.currentPathSegments.push({ name: folder.name, id: folder.id });
    attachments.readStatus = states.PENDING;
    attachments.readErrors = [];
    attachments.items = [];
  }
});

const attachmentFolderNavigate = withDraft((draft, { category, code, segments }) => {
  const { attachments } = findDatasetAttachments(draft, { category, code });
  if (attachments) {
    attachments.folders.currentPathSegments = segments;
    attachments.readStatus = states.PENDING;
    attachments.readErrors = [];
    attachments.items = [];
  }
});

const showDeleteDatasetFolder = withDraft((draft, { category, code, folder }) => {
  draft.activeFolder.open = true;
  draft.activeFolder.category = category;
  draft.activeFolder.code = code;
  draft.activeFolder.folder = folder;
});

const hideDeleteDatasetFolder = withDraft((draft) => {
  draft.activeFolder = activeFolderInitialState();
});

const deleteDatasetFolder = withDraft((draft, { folder }) => {
  draft.activeFolder.open = false;
  draft.activeFolder.folder = folder;
});

const showCreateDatasetFolder = withDraft((draft, { category, code }) => {
  draft.createFolder.open = true;
  draft.createFolder.category = category;
  draft.createFolder.code = code;
});

const hideCreateDatasetFolder = withDraft((draft) => {
  draft.createFolder = createFolderInitialState();
});

const createDatasetFolder = withDraft((draft) => {
  draft.createFolder.saveStatus = states.PENDING;
  draft.createFolder.saveErrors = [];
});

const createDatasetFolderError = withDraft((draft, action) => {
  draft.createFolder.saveStatus = states.ERROR;
  draft.createFolder.saveErrors = [responseError(action)];
});

const updateCreateFolderProperties = withDraft((draft, action) => {
  assignIfExists(draft.createFolder, action.properties);
});

const createDatasetFolderSuccess = withDraft((draft, { category, code, result }) => {
  draft.createFolder = createFolderInitialState();

  const item = findDataset(draft, { category, code });

  if (item) {
    item.attachments.items.push(result);
  }
});

export default {
  [actions.DATASET_ATTACHMENT_DOWNLOAD]: datasetAttachmentDownload,
  [actions.DATASET_ATTACHMENT_DOWNLOAD_ERROR]: datasetAttachmentDownloadSuccessOrError,
  [actions.DATASET_ATTACHMENT_DOWNLOAD_SUCCESS]: datasetAttachmentDownloadSuccessOrError,
  [actions.DATASET_ATTACHMENT_UPLOAD_REMOVED]: datasetAttachmentUploadRemoved,
  [actions.DATASET_ATTACHMENT_UPLOAD_SUCCESS]: datasetAttachmentUploadSuccess,
  [actions.DATASET_ATTACHMENTS_UPLOAD]: datasetAttachmentsUpload,
  [actions.DATASET_ATTACHMENTS_UPLOAD_FAIL]: datasetAttachmentsUploadFail,
  [actions.DELETE_DATASET_ATTACHMENT]: deleteDatasetAttachment,
  [actions.DELETE_DATASET_ATTACHMENT_ERROR]: deleteDatasetAttachmentError,
  [actions.DELETE_DATASET_ATTACHMENT_SUCCESS]: deleteDatasetAttachmentSuccess,
  [actions.FETCH_DATASET_ATTACHMENTS]: fetchDatasetAttachments,
  [actions.FETCH_DATASET_ATTACHMENTS_ERROR]: fetchDatasetAttachmentsError,
  [actions.FETCH_DATASET_ATTACHMENTS_SUCCESS]: fetchDatasetAttachmentsSuccess,
  [actions.HIDE_CONVERT_DATAFILE_DIALOG]: hideConvertDatafileDialog,
  [actions.HIDE_DELETE_DATASET_ATTACHMENT]: hideDeleteDatasetAttachment,
  [actions.HIDE_EDIT_DATASET_ATTACHMENT_DIALOG]: hideEditDatasetAttachmentDialog,
  [actions.HIDE_DOWNLOADABLE_CONFIRMATION_DIALOG]: hideDownloadableConfirmationDialog,
  [actions.SHOW_CONVERT_ATTACHMENT_DIALOG]: showConvertAttachmentDialog,
  [actions.SHOW_DATASET_ATTACHMENT_DICTIONARY]: showDatasetAttachmentDictionary,
  [actions.SHOW_DATASET_ATTACHMENT_DICTIONARY_ERROR]: showDatasetAttachmentDictionaryError,
  [actions.SHOW_DELETE_DATASET_ATTACHMENT]: showDeleteDatasetAttachment,
  [actions.SHOW_DOWNLOADABLE_CONFIRMATION_DIALOG]: showDownloadableConfirmationDialog,
  [actions.SHOW_EDIT_DATASET_ATTACHMENT_DIALOG]: showEditDatasetAttachmentDialog,
  [actions.TOGGLE_DATASET_ATTACHMENT_COLUMN_ORDER_BY]: toggleDatasetAttachmentColumnOrderBy,
  [actions.TOGGLE_DATASET_ATTACHMENT_TYPE_FILTER]: toggleDatasetAttachmentTypeFilter,
  [actions.UPDATE_ACTIVE_DICTIONARY_ATTACHMENT_PROPERTIES]: updateActiveDictionaryAttachmentProperties,
  [actions.UPDATE_DATASET_ATTACHMENT_DESCRIPTION]: updateDatasetAttachmentDescription,
  [actions.UPDATE_DATASET_ATTACHMENT_DESCRIPTION_ERROR]: updateDatasetAttachmentDescriptionError,
  [actions.UPDATE_DATASET_ATTACHMENT_DESCRIPTION_SUCCESS]: updateDatasetAttachmentDescriptionSuccess,
  [actions.UPDATE_DATASET_ATTACHMENT_METADATA]: updateDatasetAttachmentMetadata,
  [actions.UPDATE_DATASET_ATTACHMENT_METADATA_ERROR]: updateDatasetAttachmentMetadataError,
  [actions.UPDATE_DATASET_ATTACHMENT_METADATA_SUCCESS]: updateDatasetAttachmentMetadataSuccess,
  [actions.UPDATE_DATASET_ATTACHMENT_PROPERTIES]: updateDatasetAttachmentProperties,
  [actions.UPDATE_DATASET_ATTACHMENT_TEXT_FILTER]: updateDatasetAttachmentTextFilter,
  [actions.DATASET_ATTACHMENT_FOLDER_PUSH]: attachmentFolderPush,
  [actions.DATASET_ATTACHMENT_FOLDER_NAVIGATE]: attachmentFolderNavigate,
  [actions.SHOW_DELETE_DATASET_ATTACHMENT_FOLDER]: showDeleteDatasetFolder,
  [actions.HIDE_DELETE_DATASET_ATTACHMENT_FOLDER]: hideDeleteDatasetFolder,
  [actions.DELETE_DATASET_ATTACHMENT_FOLDER]: deleteDatasetFolder,
  [actions.SHOW_CREATE_DATASET_ATTACHMENT_FOLDER]: showCreateDatasetFolder,
  [actions.HIDE_CREATE_DATASET_ATTACHMENT_FOLDER]: hideCreateDatasetFolder,
  [actions.CREATE_DATASET_ATTACHMENT_FOLDER]: createDatasetFolder,
  [actions.CREATE_DATASET_ATTACHMENT_FOLDER_ERROR]: createDatasetFolderError,
  [actions.CREATE_DATASET_ATTACHMENT_FOLDER_SUCCESS]: createDatasetFolderSuccess,
  [actions.UPDATE_CREATE_DATASET_ATTACHMENT_FOLDER_PROPERTIES]: updateCreateFolderProperties,
};
